import React, { useEffect, useRef, useState } from "react";
import { filterPriceInput, normalizePriceInput } from "../productListPriceInput";

const grouped = (raw: string) => {
  const n = Number(String(raw || "").replace(/,/g, ""));
  return raw !== "" && Number.isFinite(n) ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : raw;
};

/**
 * One inline price (design D01): "₹ 55,000" at rest, plain digits while typing, "+ Add price" when
 * empty. Saves on blur or Enter; Escape puts the old value back. No animation (tapped often).
 */
const PriceInput = ({
  value,
  label,
  emptyText = "+ Add price",
  onCommit,
  className = "",
}: {
  value: unknown;
  label: string;
  emptyText?: string;
  onCommit: (next: string) => void;
  className?: string;
}) => {
  const saved = String(value ?? "").trim();
  const [text, setText] = useState(saved);
  const [editing, setEditing] = useState(false);
  const cancelled = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Select the plain digits once they replace the grouped text, so typing replaces the price.
  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  useEffect(() => {
    if (!editing) setText(saved);
  }, [saved, editing]);

  const commit = () => {
    setEditing(false);
    if (cancelled.current) {
      cancelled.current = false;
      setText(saved);
      return;
    }
    const next = normalizePriceInput(text);
    if (next === null) {
      setText(saved);
      return;
    }
    setText(next);
    if (next !== saved) onCommit(next);
  };

  const empty = !editing && text === "";
  return (
    <label
      className={`pl-price-in${empty ? " is-none" : ""}${editing ? " is-editing" : ""} ${className}`}
      onClick={(event) => event.stopPropagation()}
    >
      {!empty ? <span className="pl-price-in__cur" aria-hidden="true">₹</span> : null}
      <input
        type="text"
        inputMode="decimal"
        aria-label={label}
        placeholder={emptyText}
        value={editing ? text : grouped(text)}
        ref={inputRef}
        onFocus={() => setEditing(true)}
        onChange={(event) => setText(filterPriceInput(event.target.value))}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") (event.target as HTMLInputElement).blur();
          if (event.key === "Escape") {
            cancelled.current = true;
            (event.target as HTMLInputElement).blur();
          }
        }}
      />
    </label>
  );
};

export default PriceInput;
